import { useEffect, useRef, useState } from 'react';
import { getPhotoVaultStats, isNativePhotoVault } from '../utils/imageCache';
import { syncFullPhotoVault } from '../utils/photoVaultSync';

/**
 * Al entrar a la app con sesión: baja TODAS las fotos del catálogo al disco de la tablet.
 */
export default function useFullPhotoVaultSync({ enabled = true } = {}) {
  const started = useRef(false);
  const [progress, setProgress] = useState(null);
  const [vaultCount, setVaultCount] = useState(() => getPhotoVaultStats().saved);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!enabled || !isNativePhotoVault() || started.current) return undefined;
    started.current = true;
    let cancelled = false;
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;

    (async () => {
      // Deja que el grid visible hidrate primero (prioridad a fotos en pantalla).
      await new Promise((r) => setTimeout(r, 4500));
      if (cancelled) return;
      setBusy(true);
      try {
        const result = await syncFullPhotoVault({
          signal: ctrl?.signal,
          force: getPhotoVaultStats().saved < 30,
          onProgress: (p) => {
            if (!cancelled) setProgress(p);
          },
        });
        if (!cancelled) {
          setVaultCount(getPhotoVaultStats().saved || result.saved || 0);
          setTimeout(() => {
            if (!cancelled) setProgress(null);
          }, 4000);
        }
      } catch {
        if (!cancelled) setProgress(null);
      } finally {
        if (!cancelled) {
          setBusy(false);
          setVaultCount(getPhotoVaultStats().saved);
        }
      }
    })();

    const onOnline = () => {
      syncFullPhotoVault({
        force: false,
        onProgress: (p) => setProgress(p),
      }).then(() => {
        setVaultCount(getPhotoVaultStats().saved);
        setTimeout(() => setProgress(null), 3500);
      });
    };
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      ctrl?.abort();
      window.removeEventListener('online', onOnline);
    };
  }, [enabled]);

  return { progress, vaultCount, busy };
}
